import clsx from 'clsx';
import {
  CefrLevel,
  ProjectPageGetProjectByIdDocument,
  ScoreComponent,
  ScoreComponentKind,
  StageScoringConfig,
  useUpdateStageScoringConfigMutation,
} from 'generated/graphql';
import { FC, useState } from 'react';
import { Button } from '@spotted-zebra-uk/ui-components';
import {
  formatCefrLevel,
  formatWeightTotal,
  isWeightTotalValid,
  SCORE_COMPONENT_LABELS,
  SCORE_KIND_LABELS,
  ScoringRowDraft,
  takesThreshold,
  toMutationComponents,
  totalWeight,
  validateScoringDrafts,
} from './StageScoringPanel.helpers';
import styles from './StageScoringPanel.module.scss';

type StageScoringPanelProps = {
  /** The `SmStageModel.id` that `updateStageScoringConfig` takes. */
  stageId: number;
  projectId: number;
  /** Null before the project is published. */
  scoringConfig: StageScoringConfig | null | undefined;
  /** The stage's English action level, shown for an English knockout. */
  requiredCefrLevel: CefrLevel | null | undefined;
};

const toDrafts = (config: StageScoringConfig): ScoringRowDraft[] =>
  config.components.map(({ component, kind, weight, threshold }) => ({
    component,
    kind,
    weight: String(weight),
    threshold:
      threshold === null || threshold === undefined ? '' : String(threshold),
  }));

const StageScoringPanel: FC<StageScoringPanelProps> = ({
  stageId,
  projectId,
  scoringConfig,
  requiredCefrLevel,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [rows, setRows] = useState<ScoringRowDraft[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const [updateConfig, { loading }] = useUpdateStageScoringConfigMutation({
    refetchQueries: [
      {
        query: ProjectPageGetProjectByIdDocument,
        variables: { id: projectId },
      },
    ],
    awaitRefetchQueries: true,
  });

  const shownRows = isEditing
    ? rows
    : scoringConfig
    ? toDrafts(scoringConfig)
    : [];
  const total = totalWeight(shownRows);
  const isTotalValid = isWeightTotalValid(total);

  const startEditing = () => {
    if (!scoringConfig) return;
    setRows(toDrafts(scoringConfig));
    setErrors([]);
    setIsEditing(true);
  };

  const updateRow = (
    component: ScoreComponent,
    patch: Partial<ScoringRowDraft>
  ) =>
    setRows(current =>
      current.map(row =>
        row.component === component ? { ...row, ...patch } : row
      )
    );

  const handleSave = async () => {
    const validationErrors = validateScoringDrafts(rows);
    setErrors(validationErrors);
    if (validationErrors.length > 0) return;

    try {
      await updateConfig({
        variables: { stageId, components: toMutationComponents(rows) },
      });
      setIsEditing(false);
    } catch (error) {
      setErrors([
        error instanceof Error ? error.message : 'Something went wrong.',
      ]);
    }
  };

  return (
    <section className={styles.panel} data-testid="stage-scoring-panel">
      <div className={styles.header}>
        <div className={styles.title}>Assessment score</div>
        {scoringConfig && !isEditing && (
          <Button size="medium" onClick={startEditing}>
            Edit
          </Button>
        )}
      </div>

      {!scoringConfig ? (
        <div className={styles.note}>
          Configured when the role is published.
        </div>
      ) : (
        <>
          <div className={styles.rows}>
            {shownRows.map(row => (
              <ScoringRow
                key={row.component}
                row={row}
                isEditing={isEditing}
                requiredCefrLevel={requiredCefrLevel}
                onChange={patch => updateRow(row.component, patch)}
              />
            ))}
          </div>
          <div
            className={clsx(styles.total, !isTotalValid && styles.totalInvalid)}
            data-testid="stage-scoring-total"
          >
            Total weight: {formatWeightTotal(total)}
            {!isTotalValid && ' (must be 1)'}
          </div>
          {errors.length > 0 && (
            <ul className={styles.errors} role="alert">
              {errors.map(message => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          )}
          {isEditing && (
            <div className={styles.actions}>
              <Button
                variant="secondary"
                size="medium"
                onClick={() => setIsEditing(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button size="medium" onClick={handleSave} disabled={loading}>
                Save
              </Button>
            </div>
          )}
          <div className={styles.note}>
            Saving does not recalculate scores of candidates who already
            completed; use reprocess for that.
          </div>
        </>
      )}
    </section>
  );
};

type ScoringRowProps = {
  row: ScoringRowDraft;
  isEditing: boolean;
  requiredCefrLevel: CefrLevel | null | undefined;
  onChange: (patch: Partial<ScoringRowDraft>) => void;
};

const ScoringRow: FC<ScoringRowProps> = ({
  row,
  isEditing,
  requiredCefrLevel,
  onChange,
}) => {
  const name = SCORE_COMPONENT_LABELS[row.component];
  const id = `scoring-${row.component}`;
  const isEnglishKnockout =
    row.component === ScoreComponent.EnglishProficiency &&
    row.kind === ScoreComponentKind.Knockout;

  return (
    <div className={styles.row} data-testid={id}>
      <div className={styles.componentName}>{name}</div>

      <div className={styles.field}>
        <label htmlFor={`${id}-kind`}>Kind</label>
        {isEditing ? (
          <select
            id={`${id}-kind`}
            aria-label={`${name} kind`}
            value={row.kind}
            onChange={event =>
              onChange({ kind: event.target.value as ScoreComponentKind })
            }
          >
            {Object.values(ScoreComponentKind).map(kind => (
              <option key={kind} value={kind}>
                {SCORE_KIND_LABELS[kind]}
              </option>
            ))}
          </select>
        ) : (
          <div className={styles.readOnlyValue}>
            {SCORE_KIND_LABELS[row.kind]}
          </div>
        )}
      </div>

      <div className={styles.field}>
        <label htmlFor={`${id}-weight`}>Weight</label>
        {isEditing ? (
          <input
            id={`${id}-weight`}
            aria-label={`${name} weight`}
            type="number"
            min={0}
            max={1}
            step={0.01}
            value={row.weight}
            onChange={event => onChange({ weight: event.target.value })}
          />
        ) : (
          <div className={styles.readOnlyValue}>{row.weight}</div>
        )}
      </div>

      {takesThreshold(row) && (
        <div className={styles.field}>
          <label htmlFor={`${id}-threshold`}>Threshold</label>
          {isEditing ? (
            <input
              id={`${id}-threshold`}
              aria-label={`${name} threshold`}
              type="number"
              min={0}
              max={100}
              step={1}
              value={row.threshold}
              onChange={event => onChange({ threshold: event.target.value })}
            />
          ) : (
            <div className={styles.readOnlyValue}>
              {row.threshold === '' ? '–' : row.threshold}
            </div>
          )}
        </div>
      )}
      {isEnglishKnockout && (
        <div className={styles.field}>
          <span className={styles.fieldLabel}>Required level</span>
          <div className={styles.readOnlyValue}>
            {requiredCefrLevel ? formatCefrLevel(requiredCefrLevel) : '–'}
          </div>
        </div>
      )}
    </div>
  );
};

export default StageScoringPanel;
